import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { HiMenuAlt3, HiX } from "react-icons/hi";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const navLinks = [
    { name: "About", href: "/#about" },
    { name: "Services", href: "/#service" },
    { name: "Portfolio", href: "/#portfolio" },
    { name: "Blog", href: "/blog", isRoute: true },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const renderNavLink = (link) => {
    if (link.isRoute) {
      return (
        <Link
          to={link.href}
          className="text-sm text-neutral-dark font-medium hover:text-primary-500
                     transition-colors duration-200 relative group py-1"
        >
          {link.name}
          <span
            className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500
                       transition-all duration-300 group-hover:w-full"
          />
        </Link>
      );
    }

    // For hash links, use anchor on home page, Link otherwise
    if (isHomePage) {
      return (
        <a
          href={link.href.replace("/", "")}
          className="text-sm text-neutral-dark font-medium hover:text-primary-500
                     transition-colors duration-200 relative group py-1"
        >
          {link.name}
          <span
            className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500
                       transition-all duration-300 group-hover:w-full"
          />
        </a>
      );
    }

    return (
      <Link
        to={link.href}
        className="text-sm text-neutral-dark font-medium hover:text-primary-500
                   transition-colors duration-200 relative group py-1"
      >
        {link.name}
        <span
          className="absolute bottom-0 left-0 w-0 h-0.5 bg-primary-500
                     transition-all duration-300 group-hover:w-full"
        />
      </Link>
    );
  };

  const renderMobileNavLink = (link) => {
    if (link.isRoute) {
      return (
        <Link
          to={link.href}
          onClick={handleLinkClick}
          className="block py-3 px-4 text-neutral-dark font-medium
                     hover:bg-primary-50 hover:text-primary-500
                     rounded-lg transition-all duration-200"
        >
          {link.name}
        </Link>
      );
    }

    if (isHomePage) {
      return (
        <a
          href={link.href.replace("/", "")}
          onClick={handleLinkClick}
          className="block py-3 px-4 text-neutral-dark font-medium
                     hover:bg-primary-50 hover:text-primary-500
                     rounded-lg transition-all duration-200"
        >
          {link.name}
        </a>
      );
    }

    return (
      <Link
        to={link.href}
        onClick={handleLinkClick}
        className="block py-3 px-4 text-neutral-dark font-medium
                   hover:bg-primary-50 hover:text-primary-500
                   rounded-lg transition-all duration-200"
      >
        {link.name}
      </Link>
    );
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-nav" : "bg-white"
      }`}
    >
      <nav className={`container mx-auto pl-4 pr-1 lg:px-8 transition-all duration-300 ${
        isScrolled ? "py-1" : "py-2"
      }`}>
        <div className="flex items-center">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-3 mr-auto">
            <img
              src="/images/logo/logo.png"
              alt="HexaPi Tech"
              className="h-10 lg:h-11 transition-all duration-300"
            />
            <span className="text-lg lg:text-xl font-bold text-primary-500">
              HexaPi Tech
            </span>
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <li key={link.name}>{renderNavLink(link)}</li>
            ))}
          </ul>

          {/* CTA Button - Desktop */}
          {isHomePage ? (
            <a
              href="#contact"
              className="hidden lg:inline-flex items-center px-4 py-1.5 ml-6
                         bg-primary-500 text-white text-sm font-medium rounded-md
                         hover:bg-primary-600 transition-all duration-300"
            >
              Reach Us
            </a>
          ) : (
            <Link
              to="/#contact"
              className="hidden lg:inline-flex items-center px-4 py-1.5 ml-6
                         bg-primary-500 text-white text-sm font-medium rounded-md
                         hover:bg-primary-600 transition-all duration-300"
            >
              Reach Us
            </Link>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-neutral-dark hover:text-primary-500
                       transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? (
              <HiX className="w-7 h-7" />
            ) : (
              <HiMenuAlt3 className="w-7 h-7" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
          }`}
        >
          <div className="bg-white rounded-xl shadow-card p-4">
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.name}>{renderMobileNavLink(link)}</li>
              ))}
              <li className="pt-2">
                {isHomePage ? (
                  <a
                    href="#contact"
                    onClick={handleLinkClick}
                    className="block py-3 px-4 bg-primary-500 text-white text-center
                               font-medium rounded-lg hover:bg-primary-600
                               transition-all duration-200"
                  >
                    Reach Us
                  </a>
                ) : (
                  <Link
                    to="/#contact"
                    onClick={handleLinkClick}
                    className="block py-3 px-4 bg-primary-500 text-white text-center
                               font-medium rounded-lg hover:bg-primary-600
                               transition-all duration-200"
                  >
                    Reach Us
                  </Link>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
